import { Archive, Code2, Download, File, FileSignature, FileSpreadsheet, FileText, Folder, Image, Music, Presentation, Trash2Icon, Video } from 'lucide-react'
import { Document } from '../classes/Document'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { useNavigate } from 'react-router-dom'

export default function DocumentRow({ applicationId, _document, setDocuments, handleCheckDocuments }) {
  const navigate = useNavigate()
  const document = new Document(_document)

  const handleDownloadDocument = async () => {
    const preSignedURLResponse = await fetch(`${apiURL}/api/aws/`, {
      method: 'POST',
      headers: {
        'Authorization': account.accessToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: document.name,
        url: document.documentURL
      })
    })
    const preSignedURLData = await preSignedURLResponse.json()
    if (preSignedURLData.response.error) {
      if (preSignedURLData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleDownloadDocument() : navigate('/', { state: { expired: true } })
      } else
        console.error(preSignedURLData.response.error)
    } else
      window.open(preSignedURLData.response.signedURL, '_blank')
  }

  const handleDeleteDocument = async () => {
    const deleteResponse = await fetch(`${apiURL}/api/applications/${applicationId}/documents/${document.documentId}/`, {
      method: 'DELETE',
      headers: {
        'Authorization': account.accessToken
      }
    })
    const deleteData = await deleteResponse.json()
    if (deleteData.response.error) {
      if (deleteData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleDeleteDocument() : navigate('/', { state: { expired: true } })
      } else
        console.error(deleteData.response.error)
    } else {
      setDocuments(prev => prev.filter(doc => doc.documentId !== document.documentId))
      handleCheckDocuments()
    }
  }

  /**
   * @param {Number} size 
   */
  function formatFileSize(size) {
    if (size < 1024)
      return `${size} B`

    const kb = size / 1024
    if (kb < 1024)
      return `${kb.toFixed(1)} KB`

    const mb = kb / 1024
    if (mb < 1024)
      return `${mb.toFixed(1)} MB`

    const gb = mb / 1024
    return `${gb.toFixed(1)} GB`
  }

  /**
   * @param {String} mimetype 
   */
  const getIconByMimetype = (mimetype) => {
    if (!mimetype) return <File className='w-5 h-5 text-gray-400' />
    if (mimetype.startsWith('image/')) return <Image className='w-5 h-5 text-blue-500 ' />
    if (mimetype.startsWith('video/')) return <Video className='w-5 h-5 text-purple-500 ' />
    if (mimetype.startsWith('audio/')) return <Music className='w-5 h-5 text-green-500 ' />
    if (mimetype === 'application/pdf') return <FileSignature className='w-5 h-5 text-red-500' />
    if (mimetype.includes('word')) return <FileText className='w-5 h-5 text-indigo-500 ' />
    if (mimetype.includes('spreadsheet') || mimetype.includes('excel')) return <FileSpreadsheet className='w-5 h-5 text-green-600 ' />
    if (mimetype.includes('presentation') || mimetype.includes('powerpoint')) return <Presentation className='w-5 h-5 text-orange-500 ' />
    if (mimetype.startsWith('application/json') || mimetype.startsWith('text/html') || mimetype.includes('javascript')) return <Code2 className='w-5 h-5 text-gray-700 ' />
    if (mimetype === 'inode/directory') return <Folder className='w-5 h-5 text-yellow-600 ' />
    if (mimetype.includes('zip') || mimetype.includes('tar') || mimetype.includes('rar')) return <Archive className='w-5 h-5 text-pink-600 ' />
    return <File className='w-5 h-5 text-gray-400 ' />
  }

  return (
    <li
      key={document.documentId}
      className='w-full border border-gray-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-in slide-in-from-bottom-2 fade-in duration-300 fill-mode-both mb-3'
    >
      <div className='flex flex-row items-start sm:items-center gap-4 min-w-0 w-full sm:w-auto'>
        <div className='w-12 h-12 shrink-0 bg-slate-50 dark:bg-slate-700/50 flex items-center justify-center rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm'>
          {getIconByMimetype(document.mimetype)}
        </div>
        <div className='min-w-0 flex flex-col'>
          <p className='font-medium text-slate-800 dark:text-gray-100 truncate'>{document.name}</p>
          <p className='text-sm text-slate-500 dark:text-slate-400'>{formatFileSize(document.size)} - {document.mimetype}</p>
        </div>
      </div>
      <div className='flex items-center gap-3 sm:gap-4 w-full sm:w-auto mt-4 sm:mt-0 pt-4 sm:pt-0 border-t sm:border-0 border-gray-100/60 dark:border-slate-700 justify-end flex-shrink-0'>
        <button onClick={handleDownloadDocument} className='p-2 bg-gray-50 dark:bg-slate-700/50 hover:bg-blue-50 dark:hover:bg-blue-500/10 border border-gray-100 dark:border-slate-700 text-blue-600 dark:text-blue-400 hover:border-blue-200 dark:hover:border-blue-500/20 rounded-lg transition-colors' title='Download'>
          <Download className='w-4 h-4' />
        </button>
        <button onClick={handleDeleteDocument} className='p-2 bg-gray-50 dark:bg-slate-700/50 hover:bg-red-50 dark:hover:bg-red-500/10 border border-gray-100 dark:border-slate-700 text-red-600 dark:text-red-400 hover:border-red-200 dark:hover:border-red-500/20 rounded-lg transition-colors' title='Delete'>
          <Trash2Icon className='w-4 h-4' />
        </button>
      </div>
    </li>
  )
}